import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useProject } from '@/context/ProjectContext';
import { projectsAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { 
  FolderKanban, 
  Users, 
  Plus,
  Crown,
  Shield,
  User,
  MoreVertical,
  Pencil,
  Power,
  PowerOff,
  Trash2
} from 'lucide-react';

export default function DashboardPage() {
  const { user } = useAuth();
  const { projects, fetchProjects, selectProject } = useProject();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newProject, setNewProject] = useState({
    name: '',
    description: '',
    project_manager: '',
    contact_email: '',
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        await fetchProjects();
      } catch (err) {
        console.error('Failed to load dashboard:', err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [fetchProjects]);

  const handleProjectClick = (projectId) => {
    selectProject(projectId);
    navigate(`/project/${projectId}/about-me`);
  };

  // Edit project handlers
  const handleEditProject = (e, project) => {
    e.stopPropagation();
    setEditingProject({
      id: project.id,
      name: project.name || '',
      description: project.description || '',
      project_manager: project.project_manager || '',
      contact_email: project.contact_email || '',
    });
    setShowEditDialog(true);
  };

  const handleSaveEdit = async () => {
    if (!editingProject) return;
    try {
      await projectsAPI.update(editingProject.id, editingProject);
      toast.success('Project updated');
      setShowEditDialog(false);
      setEditingProject(null);
      await fetchProjects();
    } catch (err) {
      toast.error('Failed to update project');
    }
  };

  const handleToggleActive = async (e, project) => {
    e.stopPropagation();
    const isActive = project.is_active !== false;
    const action = isActive ? 'deactivate' : 'activate';
    
    if (!window.confirm(`Are you sure you want to ${action} this project?${isActive ? ' Users will not be able to access it.' : ''}`)) return;
    
    try {
      await projectsAPI.toggleActive(project.id);
      toast.success(`Project ${action}d`);
      await fetchProjects();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action} project`);
    }
  };

  const handleDeleteProject = async (e, projectId) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure? This will delete all project data including stakeholders and responses.')) return;
    
    try {
      await projectsAPI.delete(projectId);
      toast.success('Project deleted');
      await fetchProjects();
    } catch (err) {
      toast.error('Failed to delete project');
    }
  };

  const handleCreateProject = async () => {
    if (!newProject.name.trim()) {
      toast.error('Project name is required');
      return;
    }
    
    setCreating(true);
    try {
      const response = await projectsAPI.create(newProject);
      toast.success('Project created successfully');
      setShowCreateDialog(false);
      setNewProject({
        name: '',
        description: '',
        project_manager: '',
        contact_email: '',
      });
      await fetchProjects();
      // Navigate to the new project
      if (response.data?.id) {
        selectProject(response.data.id);
        navigate(`/project/${response.data.id}/about-me`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create project');
    } finally {
      setCreating(false);
    }
  };

  // Group projects by role
  const ownedProjects = projects.filter(p => p.user_role === 'owner');
  const adminProjects = projects.filter(p => p.user_role === 'admin');
  const memberProjects = projects.filter(p => p.user_role === 'member');

  // Calculate total invited users across owned projects
  const totalInvitedOwned = ownedProjects.reduce((sum, p) => sum + (p.invited_users_count || 0), 0);
  const maxInvitedUsers = ownedProjects[0]?.max_invited_users || 100;

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Projects
        </h1>
        <p className="text-slate-600">
          Here are the projects you're associated with
        </p>
      </div>

      {loading ? (
        <div className="space-y-6">
          <Card className="dashboard-card">
            <Skeleton className="h-48" />
          </Card>
        </div>
      ) : projects.length === 0 ? (
        <Card className="dashboard-card">
          <CardContent className="py-12 text-center">
            <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <FolderKanban className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No projects yet</h3>
            <p className="text-slate-500 mb-4">Get started by creating your first project</p>
            <Button 
              className="bg-[#5AA2AE] hover:bg-[#5AA2AE]/90" 
              data-testid="create-first-project-btn"
              onClick={() => setShowCreateDialog(true)}
            >
              <Plus className="mr-2 h-4 w-4" />
              Create Project
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6 md:space-y-8">
          {/* Role: Account Owner */}
          {ownedProjects.length > 0 && (
            <Card className="dashboard-card">
              <CardHeader className="pb-2 md:pb-3">
                <div className="flex flex-col gap-3">
                  {/* Title row */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Crown className="h-5 w-5 text-amber-500" />
                      <CardTitle className="text-base md:text-lg">Role: Account Owner</CardTitle>
                    </div>
                    <div className="hidden sm:flex items-center gap-2 text-sm">
                      <Users className="h-4 w-4 text-slate-400" />
                      <span className="font-medium">{totalInvitedOwned}</span>
                      <span className="text-slate-400">/ {maxInvitedUsers} invited</span>
                    </div>
                  </div>
                  
                  {/* Progress bar - above button */}
                  <div className="space-y-1">
                    <div className="flex sm:hidden items-center justify-between text-xs text-slate-500">
                      <span>Invited users</span>
                      <span className="font-medium">{totalInvitedOwned} / {maxInvitedUsers}</span>
                    </div>
                    <Progress 
                      value={(totalInvitedOwned / maxInvitedUsers) * 100} 
                      className="h-1.5"
                    />
                  </div>
                  
                  {/* Add Project button */}
                  <Button 
                    className="bg-[#5AA2AE] hover:bg-[#5AA2AE]/90 w-full sm:w-auto sm:self-end"
                    size="sm"
                    onClick={() => setShowCreateDialog(true)}
                    data-testid="add-project-btn"
                  >
                    <Plus className="mr-1 h-4 w-4" />
                    Add Project
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pt-0 px-3 md:px-6">
                {/* Desktop table view */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-3 font-medium text-slate-600">Project Name</th>
                        <th className="text-left py-3 px-3 font-medium text-slate-600">PM</th>
                        <th className="text-center py-3 px-3 font-medium text-slate-600">Invited</th>
                        <th className="text-center py-3 px-3 font-medium text-slate-600">Reference</th>
                        <th className="text-center py-3 px-3 font-medium text-slate-600">Pending</th>
                        <th className="text-center py-3 px-3 font-medium text-slate-600">Public</th>
                        <th className="w-12"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {ownedProjects.map((project) => (
                        <tr 
                          key={project.id}
                          className={`border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors ${project.is_active === false ? 'opacity-60' : ''}`}
                          onClick={() => handleProjectClick(project.id)}
                          data-testid={`project-row-${project.id}`}
                        >
                          <td className="py-3 px-3">
                            <div className="flex items-center gap-2">
                              <Crown className="h-4 w-4 text-amber-500 flex-shrink-0" />
                              <span className="font-medium text-slate-900">{project.name}</span>
                              {project.is_active === false && (
                                <Badge variant="secondary" className="text-xs px-1.5 py-0 bg-amber-100 text-amber-700">
                                  Deactivated
                                </Badge>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-3 text-slate-600">{project.project_manager || '--'}</td>
                          <td className="py-3 px-3 text-center">
                            <span className="inline-flex items-center justify-center min-w-[2.5rem] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">
                              {project.invited_users_count || 0}
                            </span>
                          </td>
                          <td className="py-3 px-3 text-center">
                            <span className="inline-flex items-center justify-center min-w-[2.5rem] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-medium">
                              {project.reference_count || 0}
                            </span>
                          </td>
                          <td className="py-3 px-3 text-center">
                            <span className="inline-flex items-center justify-center min-w-[2.5rem] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">
                              {project.pending_count || 0}
                            </span>
                          </td>
                          <td className="py-3 px-3 text-center">
                            <span className="inline-flex items-center justify-center min-w-[2.5rem] px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 font-medium">
                              {project.public_count || 0}
                            </span>
                          </td>
                          <td className="py-3 px-3">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  className="h-8 w-8"
                                  data-testid={`project-menu-${project.id}`}
                                >
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={(e) => handleEditProject(e, project)}>
                                  <Pencil className="mr-2 h-4 w-4" />
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={(e) => handleToggleActive(e, project)}>
                                  {project.is_active === false ? (
                                    <>
                                      <Power className="mr-2 h-4 w-4 text-green-600" />
                                      <span className="text-green-600">Activate</span>
                                    </>
                                  ) : (
                                    <>
                                      <PowerOff className="mr-2 h-4 w-4 text-amber-600" />
                                      <span className="text-amber-600">Deactivate</span>
                                    </>
                                  )}
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  className="text-red-600"
                                  onClick={(e) => handleDeleteProject(e, project.id)}
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Mobile card view */}
                <div className="md:hidden space-y-2">
                  {ownedProjects.map((project) => (
                    <div 
                      key={project.id}
                      className="p-3 bg-slate-50 rounded-lg cursor-pointer active:bg-slate-100 transition-colors"
                      onClick={() => handleProjectClick(project.id)}
                      data-testid={`project-card-mobile-${project.id}`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Crown className="h-4 w-4 text-amber-500 flex-shrink-0" />
                        <span className="font-semibold text-slate-900 text-sm">{project.name}</span>
                      </div>
                      {project.project_manager && (
                        <p className="text-xs text-slate-500 mb-2">PM: {project.project_manager}</p>
                      )}
                      <div className="grid grid-cols-4 gap-1">
                        <div className="text-center">
                          <span className="block text-base font-bold text-emerald-600">{project.invited_users_count || 0}</span>
                          <span className="text-[9px] text-slate-400 uppercase">Invited</span>
                        </div>
                        <div className="text-center">
                          <span className="block text-base font-bold text-slate-600">{project.reference_count || 0}</span>
                          <span className="text-[9px] text-slate-400 uppercase">Ref</span>
                        </div>
                        <div className="text-center">
                          <span className="block text-base font-bold text-amber-600">{project.pending_count || 0}</span>
                          <span className="text-[9px] text-slate-400 uppercase">Pending</span>
                        </div>
                        <div className="text-center">
                          <span className="block text-base font-bold text-violet-600">{project.anonymous_count || 0}</span>
                          <span className="text-[9px] text-slate-400 uppercase">Anon</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Show Add Project button even if no owned projects */}
          {ownedProjects.length === 0 && user?.role === 'owner' && (
            <Card className="dashboard-card">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Crown className="h-5 w-5 text-amber-500" />
                    <CardTitle className="text-lg">Role: Account Owner</CardTitle>
                  </div>
                  <Button 
                    className="bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
                    size="sm"
                    onClick={() => setShowCreateDialog(true)}
                    data-testid="add-project-btn"
                  >
                    <Plus className="mr-1 h-4 w-4" />
                    Add Project
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="py-8 text-center text-slate-500">
                No projects yet. Click "Add Project" to create your first project.
              </CardContent>
            </Card>
          )}

          {/* Role: Administrator */}
          {adminProjects.length > 0 && (
            <Card className="dashboard-card">
              <CardHeader className="pb-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Shield className="h-5 w-5 text-blue-500" />
                  <CardTitle className="text-lg">Role: Administrator</CardTitle>
                  <Badge 
                    variant="outline" 
                    className="text-xs cursor-help" 
                    title="Projects in another organization's account where you have admin access"
                  >
                    External
                  </Badge>
                </div>
                <p className="text-sm text-slate-500 mt-1">Projects in other accounts where you have admin access</p>
              </CardHeader>
              <CardContent className="pt-0">
                {/* Desktop table view */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-3 font-medium text-slate-600">Project Name</th>
                        <th className="text-left py-3 px-3 font-medium text-slate-600">PM</th>
                        <th className="text-left py-3 px-3 font-medium text-slate-600">Owner</th>
                        <th className="text-center py-3 px-3 font-medium text-slate-600">Invited</th>
                        <th className="text-center py-3 px-3 font-medium text-slate-600">Reference</th>
                        <th className="text-center py-3 px-3 font-medium text-slate-600">Pending</th>
                        <th className="text-center py-3 px-3 font-medium text-slate-600">Public</th>
                      </tr>
                    </thead>
                    <tbody>
                      {adminProjects.map((project) => (
                        <tr 
                          key={project.id}
                          className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
                          onClick={() => handleProjectClick(project.id)}
                          data-testid={`project-row-${project.id}`}
                        >
                          <td className="py-3 px-3">
                            <div className="flex items-center gap-2">
                              <Shield className="h-4 w-4 text-blue-500 flex-shrink-0" />
                              <span className="font-medium text-slate-900">{project.name}</span>
                            </div>
                          </td>
                          <td className="py-3 px-3 text-slate-600">{project.project_manager || '--'}</td>
                          <td className="py-3 px-3 text-slate-600">{project.account_owner_name || '--'}</td>
                          <td className="py-3 px-3 text-center">
                            <span className="inline-flex items-center justify-center min-w-[2.5rem] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">
                              {project.invited_users_count || 0}
                            </span>
                          </td>
                          <td className="py-3 px-3 text-center">
                            <span className="inline-flex items-center justify-center min-w-[2.5rem] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-medium">
                              {project.reference_count || 0}
                            </span>
                          </td>
                          <td className="py-3 px-3 text-center">
                            <span className="inline-flex items-center justify-center min-w-[2.5rem] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">
                              {project.pending_count || 0}
                            </span>
                          </td>
                          <td className="py-3 px-3 text-center">
                            <span className="inline-flex items-center justify-center min-w-[2.5rem] px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 font-medium">
                              {project.public_count || 0}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Mobile card view */}
                <div className="md:hidden space-y-2">
                  {adminProjects.map((project) => (
                    <div 
                      key={project.id}
                      className="p-3 bg-blue-50 rounded-lg cursor-pointer active:bg-blue-100 transition-colors"
                      onClick={() => handleProjectClick(project.id)}
                      data-testid={`project-card-mobile-admin-${project.id}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Shield className="h-4 w-4 text-blue-500 flex-shrink-0" />
                        <span className="font-semibold text-slate-900 text-sm">{project.name}</span>
                      </div>
                      <div className="flex flex-wrap gap-x-3 text-xs text-slate-500 mb-2">
                        {project.project_manager && <span>PM: {project.project_manager}</span>}
                        {project.account_owner_name && <span>Owner: {project.account_owner_name}</span>}
                      </div>
                      <div className="grid grid-cols-4 gap-1">
                        <div className="text-center">
                          <span className="block text-base font-bold text-emerald-600">{project.invited_users_count || 0}</span>
                          <span className="text-[9px] text-slate-400 uppercase">Invited</span>
                        </div>
                        <div className="text-center">
                          <span className="block text-base font-bold text-slate-600">{project.reference_count || 0}</span>
                          <span className="text-[9px] text-slate-400 uppercase">Ref</span>
                        </div>
                        <div className="text-center">
                          <span className="block text-base font-bold text-amber-600">{project.pending_count || 0}</span>
                          <span className="text-[9px] text-slate-400 uppercase">Pending</span>
                        </div>
                        <div className="text-center">
                          <span className="block text-base font-bold text-violet-600">{project.anonymous_count || 0}</span>
                          <span className="text-[9px] text-slate-400 uppercase">Anon</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Role: Stakeholder */}
          {memberProjects.length > 0 && (
            <Card className="dashboard-card">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <User className="h-5 w-5 text-slate-500" />
                  <CardTitle className="text-lg">Role: Stakeholder</CardTitle>
                </div>
                <p className="text-sm text-slate-500 mt-1">Projects where you're a participant</p>
              </CardHeader>
              <CardContent className="pt-0">
                {/* Desktop table view */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-3 font-medium text-slate-600">Project Name</th>
                        <th className="text-left py-3 px-3 font-medium text-slate-600">PM</th>
                        <th className="text-center py-3 px-3 font-medium text-slate-600">About Me</th>
                        <th className="text-center py-3 px-3 font-medium text-slate-600">Mapped</th>
                        <th className="text-center py-3 px-3 font-medium text-slate-600">Others</th>
                        <th className="text-center py-3 px-3 font-medium text-slate-600">Updated</th>
                      </tr>
                    </thead>
                    <tbody>
                      {memberProjects.map((project) => (
                        <tr 
                          key={project.id}
                          className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
                          onClick={() => handleProjectClick(project.id)}
                          data-testid={`project-row-${project.id}`}
                        >
                          <td className="py-3 px-3">
                            <div className="flex items-center gap-2">
                              <User className="h-4 w-4 text-slate-400 flex-shrink-0" />
                              <span className="font-medium text-slate-900 truncate">{project.name}</span>
                            </div>
                          </td>
                          <td className="py-3 px-3 text-slate-600 truncate">{project.project_manager || '--'}</td>
                          <td className="py-3 px-3 text-center">
                            <span className="font-medium text-[#5AA2AE]">
                              {project.about_me_progress != null ? `${Math.round(project.about_me_progress)}%` : '--'}
                            </span>
                          </td>
                          <td className="py-3 px-3 text-center">
                            <span className="font-medium text-emerald-600">
                              {project.stakeholders_mapped ?? '--'}
                            </span>
                          </td>
                          <td className="py-3 px-3 text-center">
                            <span className="font-medium text-violet-600">
                              {project.about_others_progress != null ? `${Math.round(project.about_others_progress)}%` : '--'}
                            </span>
                          </td>
                          <td className="py-3 px-3 text-center">
                            <span className="text-slate-600 text-xs">
                              {project.days_since_update != null ? `${project.days_since_update}d` : '--'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Mobile card view */}
                <div className="md:hidden space-y-2">
                  {memberProjects.map((project) => (
                    <div 
                      key={project.id}
                      className="p-3 bg-slate-50 rounded-lg cursor-pointer active:bg-slate-100 transition-colors"
                      onClick={() => handleProjectClick(project.id)}
                      data-testid={`project-card-mobile-member-${project.id}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <User className="h-4 w-4 text-slate-400 flex-shrink-0" />
                        <span className="font-semibold text-slate-900 text-sm">{project.name}</span>
                      </div>
                      {project.project_manager && (
                        <p className="text-xs text-slate-500 mb-2">PM: {project.project_manager}</p>
                      )}
                      
                      {/* Progress indicators - tighter spacing */}
                      <div className="space-y-1.5 mb-2">
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] text-slate-500">About Me</span>
                          <div className="flex items-center gap-1.5">
                            <div className="w-20 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-[#5AA2AE] rounded-full"
                                style={{ width: `${project.about_me_progress || 0}%` }}
                              />
                            </div>
                            <span className="text-xs font-medium text-[#5AA2AE] w-8 text-right">
                              {project.about_me_progress != null ? `${Math.round(project.about_me_progress)}%` : '--'}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] text-slate-500">About Others</span>
                          <div className="flex items-center gap-1.5">
                            <div className="w-20 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-violet-500 rounded-full"
                                style={{ width: `${project.about_others_progress || 0}%` }}
                              />
                            </div>
                            <span className="text-xs font-medium text-violet-600 w-8 text-right">
                              {project.about_others_progress != null ? `${Math.round(project.about_others_progress)}%` : '--'}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Stats row - tighter */}
                      <div className="flex items-center justify-between pt-1.5 border-t border-slate-200">
                        <div className="flex items-center gap-1 text-xs">
                          <span className="text-emerald-600 font-medium">{project.stakeholders_mapped ?? 0}</span>
                          <span className="text-slate-400">mapped</span>
                        </div>
                        <div className="text-[10px] text-slate-400">
                          {project.days_since_update != null ? `${project.days_since_update}d ago` : 'Not started'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Create Project Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Create New Project</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="name">Project Name *</Label>
                <Input
                  id="name"
                  value={newProject.name}
                  onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                  placeholder="Enter project name"
                  data-testid="project-name-input"
                />
              </div>
              <div className="col-span-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={newProject.description}
                  onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                  placeholder="Enter project description"
                  rows={3}
                  data-testid="project-description-input"
                />
              </div>
              <div>
                <Label htmlFor="project_manager">Project Manager</Label>
                <Input
                  id="project_manager"
                  value={newProject.project_manager}
                  onChange={(e) => setNewProject({ ...newProject, project_manager: e.target.value })}
                  placeholder="PM name"
                  data-testid="project-pm-input"
                />
              </div>
              <div>
                <Label htmlFor="contact_email">Contact Email</Label>
                <Input
                  id="contact_email"
                  type="email"
                  value={newProject.contact_email}
                  onChange={(e) => setNewProject({ ...newProject, contact_email: e.target.value })}
                  placeholder="email@example.com"
                  data-testid="project-email-input"
                />
              </div>
            </div>
            <p className="text-xs text-slate-400 flex items-start gap-1.5 mt-2" data-testid="create-description-ai-note">
              <span className="mt-0.5 flex-shrink-0 text-[#5AA2AE]">&#9432;</span>
              A meaningful description helps the AI contextualize responses and generate more relevant insights.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
              onClick={handleCreateProject}
              disabled={creating}
              data-testid="create-project-submit-btn"
            >
              {creating ? 'Creating...' : 'Create Project'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Project Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Edit Project</DialogTitle>
          </DialogHeader>
          {editingProject && (
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="edit-name">Project Name *</Label>
                  <Input
                    id="edit-name"
                    value={editingProject.name}
                    onChange={(e) => setEditingProject({ ...editingProject, name: e.target.value })}
                    data-testid="edit-project-name-input"
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="edit-description">Description</Label>
                  <Textarea
                    id="edit-description"
                    value={editingProject.description}
                    onChange={(e) => setEditingProject({ ...editingProject, description: e.target.value })}
                    rows={3}
                    data-testid="edit-project-description-input"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-pm">Project Manager</Label>
                  <Input
                    id="edit-pm"
                    value={editingProject.project_manager}
                    onChange={(e) => setEditingProject({ ...editingProject, project_manager: e.target.value })}
                    data-testid="edit-project-pm-input"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-email">Contact Email</Label>
                  <Input
                    id="edit-email"
                    type="email"
                    value={editingProject.contact_email}
                    onChange={(e) => setEditingProject({ ...editingProject, contact_email: e.target.value })}
                    data-testid="edit-project-email-input"
                  />
                </div>
              </div>
              <p className="text-xs text-slate-400 flex items-start gap-1.5 mt-2" data-testid="description-ai-note">
                <span className="mt-0.5 flex-shrink-0 text-[#5AA2AE]">&#9432;</span>
                A meaningful description helps the AI contextualize responses and generate more relevant insights.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEditDialog(false); setEditingProject(null); }}>
              Cancel
            </Button>
            <Button 
              className="bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
              onClick={handleSaveEdit}
              data-testid="save-project-btn"
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
